import { validators } from '@amille/simple-validators';
import { Grid } from '@material-ui/core';
import dayjs from 'dayjs';
import { Form, Formik } from 'formik';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FullPurchaseTransactionDataFragment,
    FullQuotationDataFragment,
    GetPurchaseTransactionLiveDisplayDocument,
    GetPurchaseTransactionLiveDisplayQueryResult,
    GetQuotationDocument,
    GetQuotationQueryResult,
    LiveDealerQuotesDataFragment,
    useAuthoriseLowerQuoteMutation,
    useCloseQuotationMutation,
    useCreateQuotationMutation,
    useUpdateQuotationDealersMutation,
    useUpdateQuotationMutation,
} from '../../../../api';
import { useAlert } from '../../../../components/AlertProvider';
import { useHandleError } from '../../../../utilities/handleErrors';
import useValidator from '../../../../utilities/useValidator';
import CreationForm from '../../components/Quotation/CreationForm';
import EditForm from '../../components/Quotation/EditForm';

export type QuotationProps = {
    purchaseTransaction: FullPurchaseTransactionDataFragment;
    quotation?: FullQuotationDataFragment;
    liveQuotation?: LiveDealerQuotesDataFragment;
    quotationLoading: boolean;
    refetchQuotation: GetQuotationQueryResult['refetch'];
    refetchLiveQuotation: GetPurchaseTransactionLiveDisplayQueryResult['refetch'];
};

type QuotationFormValues = {
    remarks: string;
    hideVehicleNo: boolean;
    startPeriod: Date | null;
    endPeriod: Date | null;
    dealerIds: string[];
    tagIds: string[];
    selectedDealerIds: string[];
    lowerQuoteDealerIds: string[];
};

const toDate = (value?: string | Date | null) => {
    if (!value) {
        return null;
    }

    return value instanceof Date ? value : new Date(value);
};

const QuotationTab = ({
    purchaseTransaction,
    quotation,
    liveQuotation,
    quotationLoading,
    refetchQuotation,
    refetchLiveQuotation,
}: QuotationProps) => {
    const { t } = useTranslation(['purchaseTransactionsPage', 'common']);
    const { show } = useAlert();
    const [createQuotation] = useCreateQuotationMutation();
    const [updateQuotation] = useUpdateQuotationMutation();
    const [updateQuotationDealers] = useUpdateQuotationDealersMutation({
        awaitRefetchQueries: true,
        refetchQueries: [
            {
                query: GetQuotationDocument,
                variables: { purchaseTransactionId: purchaseTransaction.id },
            },
            {
                query: GetPurchaseTransactionLiveDisplayDocument,
                variables: { purchaseTransactionId: purchaseTransaction.id },
            },
        ],
    });
    const [authoriseLowerQuote] = useAuthoriseLowerQuoteMutation();
    const [closeQuotation] = useCloseQuotationMutation();
    const isCreationMode = !quotation;
    const editSubmitAction = useRef<'details' | 'dealers' | 'lowerQuote'>('dealers');

    const onCloseQuotation = useCallback(async () => {
        if (!quotation) {
            return;
        }

        try {
            await closeQuotation({ variables: { quotationId: quotation.id } });
            show('success', t('purchaseTransactionsPage:successfulMessage.quotationClosed'));
            await refetchQuotation();
        } catch (error) {
            show('error', error instanceof Error ? error.message : String(error));
        }
    }, [closeQuotation, quotation, refetchQuotation, show, t]);

    const initialValues = useMemo<QuotationFormValues>(
        () => ({
            dealerIds: [],
            selectedDealerIds: [],
            endPeriod: toDate(quotation?.endPeriod),
            hideVehicleNo: quotation?.hideVehicleNo ?? false,
            lowerQuoteDealerIds: [],
            remarks: quotation?.remarks ?? '',
            startPeriod: toDate(quotation?.startPeriod),
            tagIds: [],
        }),
        [quotation]
    );

    const formValidator = useMemo(
        () =>
            validators.compose(
                validators.requiredDate('startPeriod'),
                validators.custom('endPeriod', (value, values, errors, context) => {
                    if (value && values.startPeriod && dayjs(value).isAfter(dayjs(values.startPeriod))) {
                        return null;
                    }

                    if (!value) {
                        return null;
                    }

                    return t('purchaseTransactionsPage:quotation.errors.endPeriodMustBeAfterStartPeriod');
                }),
                validators.custom('selectedDealerIds', (value, values, errors, context) => {
                    const isDealerSelectionSubmit = isCreationMode || editSubmitAction.current === 'dealers';

                    if (!isDealerSelectionSubmit || values.selectedDealerIds.length > 0) {
                        return null;
                    }

                    return t('purchaseTransactionsPage:quotation.errors.selectAtLeastOneDealer');
                }),
                validators.custom('lowerQuoteDealerIds', (value, values, errors, context) => {
                    if (
                        isCreationMode ||
                        editSubmitAction.current !== 'lowerQuote' ||
                        values.lowerQuoteDealerIds.length
                    ) {
                        return null;
                    }

                    return t('purchaseTransactionsPage:quotation.errors.selectAtLeastOneDealerToRequote');
                })
            ),
        [isCreationMode, t]
    );

    const validate = useValidator<QuotationFormValues>(formValidator);
    const inSessionDealerIds = quotation?.dealersInfo?.map(dealer => dealer.dealerId) ?? [];
    const dealerIdsKey = inSessionDealerIds.join(',');

    const onSubmit = useHandleError(
        async (values: QuotationFormValues) => {
            let successMessage = t('common:successfulMessage.updatesSaved');

            if (isCreationMode) {
                await createQuotation({
                    variables: {
                        fields: {
                            dealerIds: values.selectedDealerIds,
                            endPeriod: values.endPeriod,
                            hideVehicleNo: values.hideVehicleNo,
                            purchaseTransactionId: purchaseTransaction.id,
                            remarks: values.remarks,
                            startPeriod: values.startPeriod,
                            tagIds: values.tagIds,
                        },
                    },
                });
            } else if (editSubmitAction.current === 'details') {
                await updateQuotation({
                    variables: {
                        fields: {
                            endPeriod: values.endPeriod,
                            hideVehicleNo: values.hideVehicleNo,
                            remarks: values.remarks,
                            startPeriod: values.startPeriod,
                        },
                        quotationId: quotation.id,
                    },
                });
            } else if (editSubmitAction.current === 'lowerQuote') {
                await authoriseLowerQuote({
                    variables: {
                        dealerIds: values.lowerQuoteDealerIds,
                        quotationId: quotation.id,
                    },
                });

                successMessage = t('purchaseTransactionsPage:quotation.authorisationForLowerQuote.whatsAppSent', {
                    n: values.lowerQuoteDealerIds.length,
                });
            } else {
                try {
                    await updateQuotationDealers({
                        variables: {
                            fields: {
                                dealerIds: values.selectedDealerIds,
                                remarks: values.remarks,
                                tagIds: values.tagIds,
                            },
                            quotationId: quotation.id,
                        },
                    });
                } catch (error) {
                    await Promise.all([refetchQuotation(), refetchLiveQuotation()]);
                    throw error;
                }
            }

            show('success', successMessage);
            editSubmitAction.current = 'dealers';
            await Promise.all([refetchQuotation(), refetchLiveQuotation()]);
        },
        [
            createQuotation,
            authoriseLowerQuote,
            isCreationMode,
            purchaseTransaction.id,
            quotation,
            refetchQuotation,
            refetchLiveQuotation,
            show,
            t,
            updateQuotation,
            updateQuotationDealers,
        ]
    );

    if (quotationLoading) {
        return null;
    }

    return (
        <Formik
            key={`${quotation?.id ?? 'new-quotation'}-${dealerIdsKey}`}
            initialValues={initialValues}
            onSubmit={onSubmit}
            validate={validate}
            enableReinitialize
        >
            <Form>
                <Grid spacing={2} container>
                    <Grid xs={12} item>
                        {isCreationMode && <CreationForm />}
                        {!isCreationMode && (
                            <EditForm
                                inSessionDealerIdsSet={inSessionDealerIds}
                                liveQuotation={liveQuotation}
                                onAuthoriseLowerQuote={() => {
                                    editSubmitAction.current = 'lowerQuote';
                                }}
                                onCloseQuotation={onCloseQuotation}
                                onInviteDealers={() => {
                                    editSubmitAction.current = 'dealers';
                                }}
                                onSaveQuotationDetails={() => {
                                    editSubmitAction.current = 'details';
                                }}
                                quotation={quotation}
                            />
                        )}
                    </Grid>
                </Grid>
            </Form>
        </Formik>
    );
};

export default QuotationTab;
